import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ROLE_LABELS, safeRedirect } from '../config/roles';
import { Logo, Lock } from '../components/Icons';

export function PageLoader() {
  return (
    <div className="page-loader" role="status" aria-label="Loading">
      <span className="spinner spinner--dark" />
    </div>
  );
}

/**
 * Only lets signed-in users with one of `roles` through.
 * Signed-out users go to /login and come back afterwards; other roles see an access-denied page.
 * This protects the UI only — the API must authorise every request itself.
 */
export function RequireRole({ roles, children }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <PageLoader />;
  if (status !== 'authenticated') {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (!roles.includes(user.role)) return <AccessDenied />;
  return children;
}

// Login, register and forgot-password: signed-in users are sent to their dashboard.
export function PublicOnlyRoute({ children }) {
  const { status, user } = useAuth();
  const [params] = useSearchParams();
  if (status === 'loading') return <PageLoader />;
  if (status === 'authenticated') return <Navigate to={safeRedirect(params.get('next'), user.role)} replace />;
  return children;
}

export function AccessDenied() {
  const { user, home, logout } = useAuth();
  return (
    <div className="denied">
      <Link to="/" className="brand"><Logo /><span>PinParcel</span></Link>
      <div className="denied__card" role="alert">
        <div className="denied__icon"><Lock width={28} height={28} /></div>
        <h1>You don't have access to this page</h1>
        <p>
          You're signed in as <strong>{user?.name || user?.email}</strong> ({ROLE_LABELS[user?.role] || 'user'}).
          This area is for a different type of account.
        </p>
        <div className="denied__actions">
          <Link to={home} className="btn btn--primary">Go to my dashboard</Link>
          <button type="button" className="btn btn--outline" onClick={() => logout('logout')}>Sign in with another account</button>
        </div>
      </div>
    </div>
  );
}
